import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth.js";
import { ThemeProvider, ThemeToggle } from "./theme.js";
import Login from "./pages/Login.js";
import Dashboard from "./pages/Dashboard.js";
import Workspace from "./pages/Workspace.js";
import Playground from "./pages/Playground.js";
import TeacherAssignment from "./pages/TeacherAssignment.js";
import Submissions from "./pages/Submissions.js";
import GradeSubmission from "./pages/GradeSubmission.js";
import Roster from "./pages/Roster.js";
import SuperAdmin from "./pages/SuperAdmin.js";
import Faculty from "./pages/Faculty.js";
import Institution from "./pages/Institution.js";
import InstitutionCourse from "./pages/InstitutionCourse.js";
import Monitor from "./pages/Monitor.js";
import Audit from "./pages/Audit.js";
import ExamBuilder from "./pages/ExamBuilder.js";
import ExamRunner from "./pages/ExamRunner.js";
import ExamResult from "./pages/ExamResult.js";
import Library from "./pages/Library.js";
import Courses from "./pages/Courses.js";
import CourseMarks from "./pages/CourseMarks.js";
import Landing from "./pages/Landing.js";
import { DeploymentBanner } from "./components/DeploymentBanner.js";

/**
 * One destination in the top bar. A NavLink rather than a Link so the section
 * you are in is marked: every one of these looked alike before, and the bar was
 * the only navigation on the page that never said where you were.
 */
function Nav({ to, end, children }: { to: string; end?: boolean; children: React.ReactNode }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
      {children}
    </NavLink>
  );
}

function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const isSuperAdmin = user.role === "SUPER_ADMIN";

  return (
    <div className="topbar">
      {/* A super admin has no courses, so the brand takes them to their own home. */}
      <Link to={isSuperAdmin ? "/institutions" : "/"} className="brand" style={{ color: "inherit" }}>
        <span className="brand-mark" aria-hidden="true">
          CB
        </span>
        CodeBench
      </Link>
      <div className="spacer" />
      {isSuperAdmin && <Nav to="/institutions">Institutions</Nav>}
      {/* A teacher's courses are a level above any one course, so they live here
          rather than in the course tab strip. An admin's home already lists
          every course in the institution. */}
      {user.role === "TEACHER" && <Nav to="/courses">Courses</Nav>}
      {/* A student's dashboard shows one course; this is every course of their
          degree, including the finished ones the dashboard hides by default. */}
      {user.role === "STUDENT" && <Nav to="/courses">My courses</Nav>}
      {isSuperAdmin && <Nav to="/audit">Audit log</Nav>}
      {user.role === "UNIVERSITY_ADMIN" && (
        <>
          {/* Home matches every path unless it is told not to. */}
          <Nav to="/" end>
            Institution
          </Nav>
          <Nav to="/monitor">Exams</Nav>
          <Nav to="/faculty">Faculty</Nav>
        </>
      )}
      <ThemeToggle />
      <span className="who">
        {user.name}
        {user.rollNumber ? ` · ${user.rollNumber}` : ""} ·{" "}
        {user.role.toLowerCase().replace(/_/g, " ")}
      </span>
      <button
        onClick={async () => {
          await logout();
          navigate("/login");
        }}
      >
        Sign out
      </button>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Everything that belongs to an institution rather than to the platform. A
 * super admin has no place in any of it -- the API refuses them anyway, so this
 * only saves them a page of errors on the way to their own console.
 */
function Institutional({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "SUPER_ADMIN") return <Navigate to="/institutions" replace />;
  return <>{children}</>;
}

/** Platform administration: the super admin's own area. */
function Platform({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Institution-wide oversight: the university admin's own area. */
function Oversight({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "SUPER_ADMIN") return <Navigate to="/institutions" replace />;
  if (user.role !== "UNIVERSITY_ADMIN") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * "/" is the one address that means two things. Signed in it is your home --
 * whichever home your role has. Signed out it is the product's own page, rather
 * than the bounce to /login it used to be: an address someone was given by their
 * department should say what it is before it asks them who they are.
 *
 * Home means something different per role. A university admin oversees courses
 * they do not teach, so theirs is the institution console: a list of their own
 * assignments would be an accident of which course they happen to be enrolled
 * in. Everyone who writes code -- teacher and student alike -- lands in the
 * playground instead of the assignment list. It is the one page that is useful
 * before anything has been set up or handed out, and the assignment list is one
 * tab away for the times you came here to submit something.
 */
/**
 * The assignment list, which only the people who have one can be in: an admin
 * teaches nothing, so theirs would be a list of whatever they happen to be
 * enrolled in. They get the institution console they had at "/" instead.
 */
function Assignments() {
  const { user } = useAuth();
  if (user?.role === "UNIVERSITY_ADMIN") return <Navigate to="/" replace />;
  return <Dashboard />;
}

function Root() {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  if (loading) return <div className="page muted">Loading…</div>;
  if (!user) return <Landing />;
  if (user.role === "SUPER_ADMIN") return <Navigate to="/institutions" replace />;
  if (user.role === "UNIVERSITY_ADMIN") return <Institution />;
  // Carrying the query keeps an old "/?course=..." bookmark on its course.
  return <Navigate to={`/playground${search}`} replace />;
}

function Shell() {
  return (
    <>
      <DeploymentBanner />
      <TopBar />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Root />} />
        <Route
          path="/monitor"
          element={
            <Oversight>
              <Monitor />
            </Oversight>
          }
        />
        <Route
          path="/institution/courses/:id"
          element={
            <Oversight>
              <InstitutionCourse />
            </Oversight>
          }
        />
        {/* The assignment list has an address of its own now that "/" hands
            teachers and students to the playground. */}
        <Route
          path="/assignments"
          element={
            <Institutional>
              <Assignments />
            </Institutional>
          }
        />
        <Route
          path="/assignments/:id"
          element={
            <Institutional>
              <Workspace />
            </Institutional>
          }
        />
        <Route
          path="/playground"
          element={
            <Institutional>
              <Playground />
            </Institutional>
          }
        />
        <Route
          path="/assignments/:id/edit"
          element={
            <Institutional>
              <TeacherAssignment />
            </Institutional>
          }
        />
        <Route
          path="/assignments/:id/submissions"
          element={
            <Institutional>
              <Submissions />
            </Institutional>
          }
        />
        <Route
          path="/submissions/:id"
          element={
            <Institutional>
              <GradeSubmission />
            </Institutional>
          }
        />
        <Route
          path="/courses"
          element={
            <Institutional>
              <Courses />
            </Institutional>
          }
        />
        <Route
          path="/courses/:courseId/marks"
          element={
            <Institutional>
              <CourseMarks />
            </Institutional>
          }
        />
        <Route
          path="/courses/:courseId/roster"
          element={
            <Institutional>
              <Roster />
            </Institutional>
          }
        />
        <Route
          path="/institutions"
          element={
            <Platform>
              <SuperAdmin />
            </Platform>
          }
        />
        <Route
          path="/faculty"
          element={
            <Institutional>
              <Faculty />
            </Institutional>
          }
        />
        <Route
          path="/audit"
          element={
            <Protected>
              <Audit />
            </Protected>
          }
        />
        <Route
          path="/library"
          element={
            <Institutional>
              <Library />
            </Institutional>
          }
        />
        <Route
          path="/exams/:id/edit"
          element={
            <Institutional>
              <ExamBuilder />
            </Institutional>
          }
        />
        {/* Before /exams/:id, so the literal segment is not swallowed by it. */}
        <Route
          path="/exams/:id/result"
          element={
            <Institutional>
              <ExamResult />
            </Institutional>
          }
        />
        <Route
          path="/exams/:id"
          element={
            <Institutional>
              <ExamRunner />
            </Institutional>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </ThemeProvider>
  );
}
